<script setup>
import { ref, computed } from 'vue'
import { Check, Circle, ChevronDown, User } from 'lucide-vue-next'
import { status } from '../lib/status'

const props = defineProps({
  tasks: { type: Array, required: true },
})

// Agrupa por etapa preservando a ordem em que as etapas aparecem.
const groups = computed(() =>
  props.tasks.reduce((acc, task) => {
    const group = acc.find((g) => g.stage === task.stage)
    group ? group.tasks.push(task) : acc.push({ stage: task.stage, tasks: [task] })
    return acc
  }, []),
)

// Tarefa em andamento já abre expandida.
const expanded = ref(props.tasks.find((t) => t.state === 'in_progress')?.id ?? null)
const toggle = (id) => (expanded.value = expanded.value === id ? null : id)
</script>

<template>
  <p v-if="!groups.length" class="rounded-lg border border-slate-100 px-4 py-8 text-center text-sm text-slate-500">
    Nenhuma tarefa lançada nesta ordem de serviço ainda.
  </p>

  <div v-else class="space-y-5">
    <section v-for="group in groups" :key="group.stage">
      <h3 class="mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">
        {{ group.stage }}
      </h3>

      <ul class="divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-100">
        <li v-for="task in group.tasks" :key="task.id">
          <button
            type="button"
            class="flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-up-bg"
            :aria-expanded="expanded === task.id"
            @click="toggle(task.id)"
          >
            <span
              class="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
              :class="status(task.state).icon"
              aria-hidden="true"
            >
              <Check v-if="task.state === 'done'" class="h-4 w-4" stroke-width="3" />
              <Circle v-else class="h-3 w-3 fill-current" />
            </span>

            <span class="min-w-0">
              <span class="block text-sm text-up-navy">{{ task.label }}</span>
              <span class="block text-xs" :class="status(task.state).text">
                {{ status(task.state).label }}
              </span>
            </span>

            <ChevronDown
              class="ml-auto h-4 w-4 shrink-0 text-slate-400 transition-transform"
              :class="expanded === task.id && 'rotate-180'"
              aria-hidden="true"
            />
          </button>

          <div v-if="expanded === task.id" class="border-t border-slate-100 bg-up-bg px-4 py-3.5 pl-13">
            <p class="text-sm leading-relaxed text-slate-600">{{ task.note }}</p>
            <p class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <span class="flex items-center gap-1.5">
                <User class="h-3.5 w-3.5" aria-hidden="true" />{{ task.technician }}
              </span>
              <span>Atualizado em {{ task.updated_at }}</span>
            </p>
          </div>
        </li>
      </ul>
    </section>
  </div>
</template>
